import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Users, Settings, FileCheck, Lock, Eye, BarChart3, Server, Key, UserCog, ArrowRight, ExternalLink, CheckCircle2, ChevronRight } from 'lucide-react';
import Header from '../components/layout/Header';
import { Footer, TrustBadges, DebateSandbox } from '../components/marketing';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-40px' },
  transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
};

const FEATURE_CATEGORIES = [
  {
    title: 'Governance & Compliance',
    icon: Shield,
    features: [
      'Complete audit trail for every decision',
      'Role-based access control (RBAC)',
      'Data retention policies',
      'GDPR/CCPA compliance controls',
      'Training opt-out guarantees',
      'Data Processing Agreements',
      'Sub-processor transparency',
      'Incident response SLA',
      'Custom data residency',
      'Compliance dashboard',
      'Regulatory report generation',
      'Risk assessment templates',
    ],
  },
  {
    title: 'Admin Controls',
    icon: Settings,
    features: [
      'SSO/SAML integration',
      'SCIM user provisioning',
      'Directory sync (Azure AD, Okta)',
      'IP allowlisting',
      'Session management',
      'API key lifecycle management',
      'Usage quotas per team',
      'Model access governance',
      'Custom prompt policies',
      'Content filtering rules',
      'Domain verification',
      'Admin audit logs',
    ],
  },
  {
    title: 'Team Management',
    icon: Users,
    features: [
      'Workspace isolation',
      'Team playbook libraries',
      'Shared context repositories',
      'Cross-team collaboration controls',
      'Manager approval workflows',
      'Custom role definitions',
      'Team usage analytics',
      'Bulk user management',
      'Invitation link controls',
      'External sharing policies',
      'Guest access with limitations',
      'Department-level billing',
    ],
  },
  {
    title: 'Integration & API',
    icon: Server,
    features: [
      'MCP server integration',
      'REST API access',
      'Webhook notifications',
      'Slack integration',
      'GitHub context attachment',
      'Custom model endpoints',
      'BYOK (Bring Your Own Keys)',
      'SSE streaming API',
      'Batch debate execution',
      'CI/CD pipeline triggers',
      'Export to PDF/Markdown',
      'Custom branding on exports',
    ],
  },
];

const ADMIN_FEATURES = [
  {
    title: 'SSO/SAML Configuration',
    description: 'Connect your identity provider in minutes. Support for Okta, Azure AD, OneLogin, and any SAML 2.0 provider. Enforce authentication policies across your organization.',
    icon: Key,
  },
  {
    title: 'SCIM Provisioning',
    description: 'Automate user lifecycle management. When someone joins or leaves your org in your IdP, Fray accounts are created, updated, or deactivated automatically.',
    icon: UserCog,
  },
  {
    title: 'Role-Based Access Control',
    description: 'Define custom roles with granular permissions: who can create debates, access premium models, share externally, or manage team settings. Audit every permission change.',
    icon: Shield,
  },
  {
    title: 'Audit Log Explorer',
    description: 'Every action is logged: debate creation, model selection, sharing events, admin changes, export actions. Filter by user, action type, or date range. Export to SIEM.',
    icon: Eye,
  },
];

export default function EnterprisePage() {
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Header />

      {/* Hero */}
      <section className="pt-24 pb-16 px-4 md:pt-36 md:pb-24 md:px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp}>
            <TrustBadges className="mb-6" />
            <h1 className="text-display-lg md:text-display-xl font-display text-[#F5F1E8] uppercase mb-6">
              AI-Powered Decisions<br />
              <span className="text-[#FF4D00]">With Enterprise Governance</span>
            </h1>
            <p className="text-lg text-[rgba(245,241,232,0.72)] max-w-2xl mb-8 font-body leading-relaxed">
              48 enterprise features purpose-built for organizations that need auditable AI decisions,
              governed model access, and compliance-ready infrastructure.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="https://calendly.com/fray-ai"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-6 py-3 bg-[#FF4D00] text-[#0A0A0A] font-display text-sm font-bold uppercase tracking-wider hover:bg-[#FF6B33] transition-colors"
              >
                Talk to Our Team
                <ExternalLink className="w-4 h-4" />
              </a>
              <Link
                to="/security"
                className="flex items-center justify-center gap-2 px-6 py-3 border border-[rgba(245,241,232,0.08)] text-[#F5F1E8] font-display text-sm font-bold uppercase tracking-wider hover:border-[#FF4D00] transition-colors"
              >
                View Security
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 48 Features by Category */}
      <section className="py-16 px-4 md:px-6 border-t border-[rgba(245,241,232,0.08)]">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp}>
            <div className="flex items-center gap-3 mb-8">
              <span className="text-xs font-mono text-[#FF4D00]">01</span>
              <div className="w-8 h-px bg-[#FF4D00]/40" />
              <h2 className="text-display-sm font-display text-[#F5F1E8] uppercase">48 Enterprise Features</h2>
            </div>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-6">
            {FEATURE_CATEGORIES.map((cat, ci) => (
              <motion.div
                key={cat.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: ci * 0.1 }}
                className="bg-[#1E1E1E] border border-[rgba(245,241,232,0.08)] p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <cat.icon className="w-5 h-5 text-[#FF4D00]" />
                  <h3 className="text-sm font-display font-bold text-[#F5F1E8] uppercase tracking-wider">{cat.title}</h3>
                  <span className="ml-auto text-[10px] font-mono text-[rgba(245,241,232,0.32)]">{cat.features.length}</span>
                </div>
                <div className="grid grid-cols-1 gap-1.5">
                  {cat.features.map(f => (
                    <div key={f} className="flex items-start gap-2">
                      <CheckCircle2 className="w-3 h-3 text-[#22C55E] shrink-0 mt-0.5" />
                      <span className="text-xs text-[rgba(245,241,232,0.72)]">{f}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Admin Controls Deep-Dive */}
      <section className="py-16 px-4 md:px-6 border-t border-[rgba(245,241,232,0.08)]">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp}>
            <div className="flex items-center gap-3 mb-8">
              <span className="text-xs font-mono text-[#FF4D00]">02</span>
              <div className="w-8 h-px bg-[#FF4D00]/40" />
              <h2 className="text-display-sm font-display text-[#F5F1E8] uppercase">Admin Controls</h2>
            </div>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-4">
            {ADMIN_FEATURES.map((feat, i) => (
              <motion.div
                key={feat.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="p-5 bg-[#1E1E1E] border border-[rgba(245,241,232,0.08)] hover:border-[#FF4D00]/20 transition-colors"
              >
                <feat.icon className="w-5 h-5 text-[#00D4FF] mb-3" />
                <h4 className="text-sm font-mono text-[#F5F1E8] mb-2 font-medium">{feat.title}</h4>
                <p className="text-xs text-[rgba(245,241,232,0.64)] leading-relaxed font-body">{feat.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Governance Sandbox */}
      <section className="py-16 px-4 md:px-6 border-t border-[rgba(245,241,232,0.08)]">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp}>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-mono text-[#FF4D00]">03</span>
              <div className="w-8 h-px bg-[#FF4D00]/40" />
              <h2 className="text-display-sm font-display text-[#F5F1E8] uppercase">See Governance in Action</h2>
            </div>
            <p className="text-sm text-[rgba(245,241,232,0.72)] mb-8 max-w-2xl font-body">
              Try a governance-focused debate. See how Fray produces audit trails, tracks dissent,
              and generates decision memos that satisfy compliance requirements.
            </p>
            <DebateSandbox governance={true} />
          </motion.div>
        </div>
      </section>

      {/* Enterprise Pricing Preview */}
      <section className="py-16 px-4 md:px-6 border-t border-[rgba(245,241,232,0.08)]">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp}>
            <div className="flex items-center gap-3 mb-8">
              <span className="text-xs font-mono text-[#FF4D00]">04</span>
              <div className="w-8 h-px bg-[#FF4D00]/40" />
              <h2 className="text-display-sm font-display text-[#F5F1E8] uppercase">Enterprise Pricing</h2>
            </div>
            <div className="bg-[#1E1E1E] border border-[rgba(245,241,232,0.08)] p-8 max-w-lg">
              <div className="flex items-baseline gap-2 mb-4">
                <span className="text-display-md font-display text-[#FF4D00]">CUSTOM</span>
              </div>
              <ul className="space-y-2 mb-6">
                {['Minimum 10 seats', 'Annual contract required', '50+ seats: volume pricing available', 'SSO/SAML included', 'Dedicated support channel', 'Custom model governance'].map(item => (
                  <li key={item} className="flex items-center gap-2 text-sm text-[rgba(245,241,232,0.72)]">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#22C55E] shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <a
                href="https://calendly.com/fray-ai"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-[#FF4D00] text-[#0A0A0A] font-display text-sm font-bold uppercase tracking-wider hover:bg-[#FF6B33] transition-colors"
              >
                Talk to Our Team
                <ChevronRight className="w-4 h-4" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
